import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import { queries, useAuthConfig, useAuthStore } from "@theprelude/core";
import { Input, ScrollArea, ScrollAreaViewport } from "@theprelude/ds";
import { useMemo, useRef, useState } from "react";
import { AppErrorPage } from "@/components/app-shell/app-shell";
import AuthPage from "@/components/auth-page";
import { Highlight } from "@/components/highlight";
import { ensureAuth } from "@/lib/auth";

export const Route = createFileRoute("/account/select")({
  beforeLoad: async ({ context }) => {
    const authConfig = await ensureAuth(context.auth);

    const accounts = await context.queryClient.ensureQueryData(
      queries.iam.listAccounts(authConfig),
    );

    if (accounts.length === 1) {
      const account = accounts.at(0);
      if (account) {
        context.auth.changeAccount(account.account_id);
        return redirect({ to: "/home" });
      }
    }
  },
  errorComponent: (props) => <AppErrorPage {...props} fullScreen />,
  component: AccountSelectPage,
});

const AccountItemHeight = 57;
function AccountSelectPage() {
  const authConfig = useAuthConfig();
  const changeAccount = useAuthStore((state) => state.changeAccount);
  const navigate = useNavigate();
  const accounts = useSuspenseQuery(queries.iam.listAccounts(authConfig));
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  const [searchTerm, setSearchTerm] = useState("");

  const filteredAccounts = useMemo(() => {
    return (
      accounts.data?.filter(
        (account) =>
          account.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
          account.account_id.toLowerCase().includes(searchTerm.toLowerCase()),
      ) ?? []
    );
  }, [accounts.data, searchTerm]);

  const rowVirtualizer = useVirtualizer({
    count: filteredAccounts.length,
    estimateSize: () => AccountItemHeight,
    getScrollElement: () => scrollViewportRef.current,
    overscan: AccountItemHeight,
  });

  const scrollAreaHeight = Math.min(
    filteredAccounts.length * AccountItemHeight,
    5 * AccountItemHeight,
  );

  return (
    <AuthPage title="Choose Account">
      <main>
        {accounts.data?.length === 0 && (
          <div className="flex flex-col gap-4">
            <p className="text-center text-secondary-foreground text-sm">
              No accounts found
            </p>
          </div>
        )}
        <div className="flex flex-col overflow-hidden">
          {accounts.data.length >= 6 && (
            <div className="p-4">
              <Input
                type="text"
                placeholder="Search accounts..."
                className="w-full rounded-md border border-stroke-table p-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}
          <ScrollArea style={{ height: `${scrollAreaHeight}px` }}>
            <ScrollAreaViewport ref={scrollViewportRef}>
              {filteredAccounts.length === 0 ? (
                <div className="p-8 text-center text-secondary-foreground">
                  No accounts found
                </div>
              ) : (
                <div
                  style={{
                    height: `${rowVirtualizer.getTotalSize()}px`,
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                    const account = filteredAccounts[virtualItem.index];
                    return (
                      <div
                        key={virtualItem.key}
                        data-index={virtualItem.index}
                        ref={rowVirtualizer.measureElement}
                        style={{
                          position: "absolute",
                          transform: `translateY(${virtualItem.start}px)`,
                          width: "100%",
                        }}
                        className="flex cursor-pointer flex-col border-stroke-line border-b px-4 py-2 hover:bg-secondary-foreground/10"
                        role="button"
                        onClick={() => {
                          if (account) {
                            changeAccount(account.account_id);
                            void navigate({ to: "/home" });
                          }
                        }}
                      >
                        <p className="font-semibold text-base">
                          <Highlight
                            highlight={searchTerm}
                            text={account?.company ?? ""}
                          />
                        </p>
                        <span className="text-secondary-foreground text-xs">
                          <Highlight
                            highlight={searchTerm}
                            text={account?.account_id ?? ""}
                          />
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollAreaViewport>
          </ScrollArea>
        </div>
        <div className="mt-1 flex w-full flex-col items-end gap-4">
          <Link to="/sign-out">Cancel</Link>
        </div>
      </main>
    </AuthPage>
  );
}
